import { useState, useEffect } from 'react';
import { usePopper } from 'react-popper';

interface NavbarTooltipProps {
  title: string;
  children: React.ReactNode;
  hide?: boolean;
  placement?: 'right' | 'right-start';
  offset?: [number, number];
}

const NavbarTooltip = ({
  title,
  children,
  hide,
  placement = 'right',
  offset = [0, 10],
}: NavbarTooltipProps) => {
  const [visible, setVisible] = useState(false);
  // Flag to delay tooltip rendering until Popper refs (trigger + tooltip) are attached
  const [readyToShow, setReadyToShow] = useState(false);
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      placement,
      strategy: 'fixed',
      modifiers: [
        { name: 'offset', options: { offset } },
        { name: 'eventListeners', options: { scroll: true, resize: true } },
        { name: 'zIndex', options: { padding: 1000 } },
      ],
    },
  );

  // Show tooltip only after refs are available, so Popper can calculate correct position
  useEffect(() => {
    if (visible) {
      setReadyToShow(true);
      update?.();
    } else {
      setReadyToShow(false);
    }
  }, [visible, update]);

  return (
    <div
      ref={setReferenceElement}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {!hide && readyToShow && (
        <div
          ref={setPopperElement}
          style={{
            ...styles.popper,
            maxWidth: 'calc(100vw - 16px)',
          }}
          {...attributes.popper}
          className={`pointer-events-none z-[100] transition-opacity duration-150 ${
            visible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="relative whitespace-nowrap rounded bg-background-base-surface-0 p-4 text-content-base-default shadow-elevation3">
            {title}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 scale-x-[0.84] transform">
              <div className="mt -ml-[9px] h-[17px] w-[17px] rotate-45 bg-background-base-surface-0"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavbarTooltip;
